import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
  Icon,
  Switch,
} from "@chakra-ui/react";
import { useMemo, useEffect, useState } from "react";
import { RebateHeader } from "./RebateHeader";
import { PreviewCardsSection } from "./PreviewCardsSection";
import actions from "../../actions";
import { calculatePreviewData as calculatePreviewDataFromAPI } from "../utils/feeCalculations";

interface RebateFormProps {
  // Current rebate value (in bips) or null if no rebate
  currentRebate?: { amount: number } | null;
  // Callbacks
  onEdit?: () => void;
  onRemove?: () => void;
  onSave: (rebate: {
    mode: "percentage";
    amount: number;
  }) => void | Promise<void>;
  // Form state (only used when editing)
  amount: string;
  onAmountChange: (amount: string) => void;
  isLoading?: boolean;
  // Display props
  title?: string;
  description?: string;
  headerTitle?: string;
  rebateTitle?: string;
  headerActions?: React.ReactNode;
  // Preview calculation
  calculatePreviewData?: (rebate: { amount: number }) =>
    | Array<{
        faceValue: number;
        fees: number;
        rebate: number;
        finalPrice: number;
      }>
    | Promise<
        Array<{
          faceValue: number;
          fees: number;
          rebate: number;
          finalPrice: number;
        }>
      >;
  formatRebateDisplay?: (rebate: { amount: number }) => string;
  // Force edit mode on mount (for event override)
  initialEditMode?: boolean;
  // Event override specific props
  showEnableSwitch?: boolean;
  isEnabled?: boolean;
  onEnableChange?: (enabled: boolean) => void;
  currentRebateInfo?: {
    source: "event" | "organization";
    mode?: "percentage" | "fixed";
    amount: number;
  };
  banner?: React.ReactNode | null;
  previewCardsBgColor?: string;
}

const RebateForm: React.FC<RebateFormProps> = ({
  currentRebate,
  onEdit,
  onRemove,
  onSave,
  amount,
  onAmountChange,
  isLoading = false,
  title = "Global Rebates",
  description = "Applied to all active ticket types",
  headerTitle = "Global Rebates",
  rebateTitle = "Current Rebate",
  headerActions,
  calculatePreviewData,
  formatRebateDisplay,
  initialEditMode = false,
  showEnableSwitch = false,
  isEnabled = true,
  onEnableChange,
  currentRebateInfo,
  banner,
  previewCardsBgColor = "#f5f5f5",
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);

  // State for preview data in read-only mode
  const [previewDataReadOnly, setPreviewDataReadOnly] = useState<
    Array<{
      faceValue: number;
      fees: number;
      rebate: number;
      finalPrice: number;
    }>
  >([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // State for preview data when editing (using API)
  const [previewDataEditing, setPreviewDataEditing] = useState<
    Array<{
      faceValue: number;
      fees: number;
      rebate: number;
      finalPrice: number;
      isLoading?: boolean;
    }>
  >([]);
  const [isCalculatingPreview, setIsCalculatingPreview] = useState(false);

  // Reset editing state when currentRebate changes (unless initialEditMode is set)
  useEffect(() => {
    if (!currentRebate && !initialEditMode) {
      setIsEditing(false);
    }
  }, [currentRebate, initialEditMode]);

  // Update isEditing when initialEditMode changes
  useEffect(() => {
    if (initialEditMode) {
      setIsEditing(true);
    }
  }, [initialEditMode]);

  // Default calculatePreviewData implementation - always uses API
  // Uses organization-level calculation (no event_id) by default
  const defaultCalculatePreviewData = useMemo(() => {
    return async (rebate: { amount: number }) => {
      return calculatePreviewDataFromAPI(
        rebate,
        actions.ADMIN.calculateFinalTicketPrice,
        undefined // no event_id for organization-level calculation
      );
    };
  }, []);

  // Use provided calculatePreviewData or fall back to default
  const effectiveCalculatePreviewData =
    calculatePreviewData || defaultCalculatePreviewData;

  // Load preview data when in read-only mode
  useEffect(() => {
    if (
      currentRebate &&
      !isEditing &&
      !initialEditMode &&
      effectiveCalculatePreviewData
    ) {
      setIsLoadingPreview(true);
      const result = effectiveCalculatePreviewData(currentRebate);
      if (result instanceof Promise) {
        result
          .then((data) => {
            setPreviewDataReadOnly(data);
            setIsLoadingPreview(false);
          })
          .catch((error) => {
            console.error("Error calculating preview data:", error);
            setIsLoadingPreview(false);
          });
      } else {
        setPreviewDataReadOnly(result);
        setIsLoadingPreview(false);
      }
    }
  }, [
    currentRebate,
    isEditing,
    initialEditMode,
    effectiveCalculatePreviewData,
  ]);

  // Calculate preview data when editing (using API with current rebate amount)
  // Always calculates, even if rebate is 0 or empty (uses 0 rebate in that case)
  useEffect(() => {
    if (!isEditing || !effectiveCalculatePreviewData) {
      setPreviewDataEditing([]);
      return;
    }

    let cancelled = false;
    setIsCalculatingPreview(true);

    const timeoutId = setTimeout(async () => {
      try {
        // Convert percentage to bips for API, default to 0 if empty or invalid
        const amountNum =
          amount && !isNaN(parseFloat(amount))
            ? Math.max(0, parseFloat(amount)) // Ensure non-negative
            : 0;
        const rebateBips = Math.round(amountNum * 100);

        const result = effectiveCalculatePreviewData({ amount: rebateBips });
        const data = result instanceof Promise ? await result : result;

        if (!cancelled) {
          setPreviewDataEditing(
            data.map((item) => ({
              ...item,
              isLoading: false,
            }))
          );
          setIsCalculatingPreview(false);
        }
      } catch (error) {
        console.error("Error calculating preview data:", error);
        if (!cancelled) {
          setPreviewDataEditing([]);
          setIsCalculatingPreview(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [amount, isEditing, effectiveCalculatePreviewData]);

  // Preview data for $10, $20, $50 tickets (when editing) - all from API
  const previewData = useMemo(() => {
    if (!isEditing) return [];
    return previewDataEditing;
  }, [isEditing, previewDataEditing]);

  const hasValidRebate =
    amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;

  // Default format function
  const defaultFormatRebateDisplay = (rebate: { amount: number }): string => {
    const percentage = rebate.amount / 100;
    return `${percentage.toFixed(2)}%`;
  };

  const formatDisplay = formatRebateDisplay || defaultFormatRebateDisplay;

  // Handle save
  const handleSave = async () => {
    if (!hasValidRebate) return;
    const amountNum = parseFloat(amount);
    const rebateAmount = Math.round(amountNum * 100); // Convert to bips
    await onSave({ mode: "percentage", amount: rebateAmount });
    setIsEditing(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    if (currentRebate) {
      onAmountChange((currentRebate.amount / 100).toFixed(2));
    } else {
      onAmountChange("");
    }
    // If initialEditMode was set, call onEdit to notify parent
    // (for event override case where parent needs to hide the form)
    if (initialEditMode) {
      onEdit?.();
    }
  };

  // Handle edit click
  const handleEdit = () => {
    if (currentRebate) {
      onAmountChange((currentRebate.amount / 100).toFixed(2));
    }
    setIsEditing(true);
    onEdit?.();
  };

  // Empty state - only show if no currentRebate and no headerActions (which means it's not event settings)
  // If headerActions is provided, we should show read-only state even if currentRebate is null
  // (the parent will handle showing org rebate or empty state)
  if (!currentRebate && !isEditing && !headerActions) {
    return (
      <Box
        p={6}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <RebateHeader
          title={headerTitle}
          description={description}
          actions={
            headerActions || (
              <Button
                onClick={handleEdit}
                bg="gray.100"
                border="none"
                color="gray.700"
                _hover={{ bg: "gray.200" }}
                size="sm"
              >
                Add Rebate
              </Button>
            )
          }
        />
        <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
          No rebates active
        </Text>
      </Box>
    );
  }

  // Read-only state - show if currentRebate exists and not editing
  // In event settings, the parent passes org rebate as currentRebate when no event rebate exists
  // Skip read-only if initialEditMode is true (force form view)
  if (
    currentRebate &&
    !isEditing &&
    !initialEditMode &&
    effectiveCalculatePreviewData
  ) {
    return (
      <Box
        p={6}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <VStack spacing={6} align="stretch">
          <RebateHeader
            title={headerTitle}
            description={description}
            actions={
              headerActions || (
                <>
                  <Button
                    size="sm"
                    onClick={handleEdit}
                    isDisabled={isLoading}
                    bg="gray.100"
                    border="none"
                    color="gray.700"
                    _hover={{ bg: "gray.200" }}
                  >
                    Edit
                  </Button>
                  {onRemove && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onRemove}
                      isLoading={isLoading}
                      isDisabled={isLoading}
                      borderColor="gray.300"
                      color="gray.700"
                      _hover={{ borderColor: "gray.400" }}
                    >
                      Remove
                    </Button>
                  )}
                </>
              )
            }
          />

          <VStack spacing={4} align="stretch">
            <PreviewCardsSection
              previewCardsBgColor={previewCardsBgColor}
              title={rebateTitle}
              previews={previewDataReadOnly}
              percentage={currentRebate ? currentRebate?.amount / 100 : 0}
              isCalculating={isLoadingPreview}
            />

            {banner}
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Form state (editing)
  return (
    <VStack spacing={6} align="stretch">
      {/* Current Rebate Info (for event overrides) */}
      {showEnableSwitch && currentRebateInfo && (
        <Box
          p={3}
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
        >
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Current Rebate:{" "}
            {currentRebateInfo.source === "event"
              ? "Event Override"
              : "Organization Level"}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {currentRebateInfo.amount}%
          </Text>
        </Box>
      )}

      {/* Enable Switch (for event overrides) */}
      {showEnableSwitch && (
        <FormControl>
          <FormLabel>
            <HStack>
              <Switch
                isChecked={isEnabled}
                onChange={(e) => onEnableChange?.(e.target.checked)}
                isDisabled={isLoading}
              />
              <Text>Override organization rebate for this event</Text>
            </HStack>
          </FormLabel>
          <FormHelperText>
            When enabled, this rebate will override the organization-level
            rebate for this event only.
          </FormHelperText>
        </FormControl>
      )}

      {/* Rebate Configuration */}
      {(isEnabled || !showEnableSwitch) && (
        <Box
          p={6}
          bg="white"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        >
          <VStack spacing={4} align="stretch">
            {/* Title and Description */}
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold">
                {title}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {description}
              </Text>
            </VStack>

            {/* Input Field */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">
                Rebate Percentage
              </FormLabel>
              <Box position="relative" w="100%">
                <Input
                  type="number"
                  value={amount || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !isNaN(parseFloat(value))) {
                      const numValue = parseFloat(value);
                      if (numValue > 25) {
                        onAmountChange("25");
                        return;
                      }
                    }
                    onAmountChange(value);
                  }}
                  placeholder="0"
                  isDisabled={isLoading}
                  min={0}
                  max={25}
                  step="0.01"
                  pl="40px"
                  pr="4"
                  borderColor="gray.300"
                  w="100%"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }}
                />
                <Text
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.500"
                  fontSize="sm"
                  pointerEvents="none"
                  fontWeight="medium"
                >
                  %
                </Text>
              </Box>
              <FormHelperText>Maximum rebate percentage is 25%</FormHelperText>
            </FormControl>

            {/* Fan Price Preview */}
            <PreviewCardsSection
              previewCardsBgColor={previewCardsBgColor}
              title={isEditing ? "Fan Price Preview" : "Current Rebate"}
              previews={previewData}
              percentage={amount ? parseFloat(amount) : 0}
              isCalculating={isCalculatingPreview}
            />

            {/* Action Buttons */}
            <HStack spacing={3} w="100%">
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={isLoading}
                borderColor="gray.300"
                bg="white"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                isLoading={isLoading}
                isDisabled={
                  isLoading || (showEnableSwitch ? false : !hasValidRebate)
                }
                bg={showEnableSwitch || hasValidRebate ? "gray.900" : "#1A202C"}
                color={"white"}
                flex={1}
                _hover={
                  showEnableSwitch || hasValidRebate
                    ? { bg: "gray.800" }
                    : { bg: "gray.300", cursor: "not-allowed" }
                }
              >
                {showEnableSwitch && !isEnabled
                  ? "Clear Override"
                  : currentRebate
                  ? "Update Rebate"
                  : "Activate Rebate"}
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

export default RebateForm;
